import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Typography, Box, Stack, Alert, CircularProgress } from "@mui/material";
import { Add, Download } from "@mui/icons-material";

import DesignScoreComponent from "../../DesignPhase/Step_2_Designs/DesignScoreComponent";
import { createDesign, downloadDesignExport, patchDesign } from "../../DesignPhase/DesignActions";
import DesignDetails from "../../DesignPhase/Step_2_Designs/DesignDetails";
import ToggleableInputField from "../../../CustomComponents/InputFields/ToggleableInputField";
import { Link } from "react-router-dom";

export default function LoopReference({ loopId, editable = true }) {
    const dispatch = useDispatch();
    const { selectedProject } = useSelector((state) => state.projects);
    const { loading } = useSelector((state) => state.loop);
    
    const [creatingDesign, setCreatingDesign] = useState(false);

    // Find the current loop from the project data
    const derivedLoop = useMemo(() => {
        if (!selectedProject || !loopId) return null;
        for (const phase of selectedProject.phases || []) {
            const found = (phase.loops || []).find((l) => l.id === loopId);
            if (found) return { ...found, phase };
        }
        return null;
    }, [selectedProject, loopId]);

    const referenceDesign = derivedLoop?.reference_design;
    const phaseID = derivedLoop?.phase?.id;

    const handleCreateDesign = async () => {
        if (!derivedLoop) return;
        
        setCreatingDesign(true);
        try {
            const data = {
                description: "",
                is_preferred: false,
                is_reference: true,
                loop: loopId,
                name: `Referentie`,
                cost: 0,
                scores: [],
            };
            
            // Create the design and then update the loop to reference it
            createDesign(data, phaseID, loopId, dispatch, setCreatingDesign);
        } catch (error) {
            console.error("Error creating loop reference design:", error);
            setCreatingDesign(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    if (!derivedLoop) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                Ontwerpronde niet gevonden
            </Alert>
        );
    }

    return (
        <Box>
            <Typography variant="h6" component="h2" gutterBottom>
                Referentieontwerp
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Maak een referentieontwerp specifiek voor deze ontwerpronde. 
                Dit ontwerp kan gebruikt worden om de ronde-specifieke KPI's te scoren.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Voor de berekening van de milieu-impact (MKI) en CO2-eq kan de <Link to="https://rekenmachinekengetallen.duurzaamhwbp.nl/" target="_blank">Rekenmachine kengetallen</Link> gebruikt worden.
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight='bold' paragraph>Het is aan te bevelen om kort toe te lichten op welke informatie het referentieontwerp is gebaseerd.</Typography>

            {referenceDesign && Object.keys(referenceDesign).length > 0 ? (
                <>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <ToggleableInputField
                            label="Naam"
                            field="name"
                            value={referenceDesign.name}
                            header={true}
                            onSave={(data) => patchDesign({ ...data, id: referenceDesign.id }, phaseID, referenceDesign.loop, dispatch)}
                            allowEdit={true}
                        />
                        <Button 
                            onClick={() => downloadDesignExport(referenceDesign)}
                            startIcon={<Download />}
                            size="small"
                        >
                            Download
                        </Button>
                    </Stack>
                    
                    <DesignDetails 
                        design={referenceDesign} 
                        payload={{ project: selectedProject, editable }}
                        loop={derivedLoop} 
                        phaseID={phaseID} 
                        expanded={true} 
                    />
                    
                    <DesignScoreComponent
                        project={selectedProject}
                        design={referenceDesign}
                        designScores={referenceDesign.scores}
                        phaseID={phaseID}
                        loopID={loopId}
                        editable={editable}
                    />
                </>
            ) : (
                <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Er is nog geen referentieontwerp voor deze ontwerpronde. 
                        Een referentieontwerp helpt bij het vergelijken van alternatieve ontwerpen binnen deze ontwerpronde.
                    </Alert>
                    
                    {editable && (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleCreateDesign}
                            disabled={creatingDesign}
                        >
                            {creatingDesign ? "Bezig met aanmaken..." : "Referentieontwerp toevoegen"}
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
}
